import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { PlayCircle } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';

const VideosPage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const { userTheme } = useTheme();

  // Get theme values
  const pageStyles = userTheme?.page || {};
  const educationBox = userTheme?.educationBox || {};
  const buttonStyles = userTheme?.buttons || {};
  
  // Fallback colors
  const primaryColor = buttonStyles.primaryColor || '#2B4B6A';
  const cardBg = educationBox.backgroundColor || '#FFFFFF';
  const cardBorder = educationBox.borderColor || '#E5E7EB';
  const textColor = educationBox.textColor || '#374151';
  const titleColor = educationBox.titleColor || '#111827';
  const playButtonColor = educationBox.playButtonColor || primaryColor;

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await api.get('/videos');
      setVideos(response.data);
    } catch (error) {
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  // Extract YouTube video ID from URL
  const getYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url?.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  if (loading) {
    return (
      <Sidebar>
        <div 
          className="min-h-screen flex items-center justify-center"
          style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
        >
          <div 
            className="animate-spin rounded-full h-12 w-12 border-2 border-gray-300"
            style={{ borderTopColor: primaryColor }}
          ></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div 
        className="p-8 min-h-screen"
        style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
      >
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-xl" style={{ backgroundColor: primaryColor }}>
              <PlayCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 
                className="text-2xl font-semibold"
                style={{ color: pageStyles.titleColor || '#111827' }}
              >
                Educational Videos
              </h1>
              <p style={{ color: pageStyles.textColor || '#6B7280' }} className="text-sm">
                Learn more about commodity trading and our platform
              </p>
            </div>
          </div>
        </div>

        {videos.length === 0 ? (
          <div 
            className="rounded-xl p-12 text-center"
            style={{ backgroundColor: cardBg, border: `1px solid ${cardBorder}` }}
          >
            <PlayCircle className="w-16 h-16 mx-auto mb-4" style={{ color: cardBorder }} />
            <h3 className="text-lg font-medium mb-2" style={{ color: titleColor }}>
              No videos available
            </h3>
            <p style={{ color: textColor }}>Check back soon for educational content.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {videos.map((video) => {
              // Support both 'url' and 'youtube_url' field names
              const videoUrl = video.youtube_url || video.url;
              const videoId = getYouTubeId(videoUrl);
              return (
                <div 
                  key={video.id} 
                  className="rounded-xl shadow-sm overflow-hidden"
                  style={{ backgroundColor: cardBg, border: `1px solid ${cardBorder}` }}
                >
                  {videoId ? (
                    <div className="relative pt-[56.25%]">
                      <iframe
                        className="absolute inset-0 w-full h-full"
                        src={`https://www.youtube.com/embed/${videoId}`}
                        title={video.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : videoUrl ? (
                    <div className="relative pt-[56.25%]">
                      <iframe
                        className="absolute inset-0 w-full h-full"
                        src={videoUrl}
                        title={video.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : null}
                  <div className="p-5">
                    <h3 className="font-semibold mb-2" style={{ color: titleColor }}>
                      {video.title}
                    </h3>
                    {video.description && (
                      <p className="text-sm" style={{ color: textColor }}>{video.description}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default VideosPage;
